import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiSend, FiEye, FiAlertCircle } from 'react-icons/fi';
import RecipientPicker from './RecipientPicker';

/**
 * Compose a mass email.
 *
 * Can be pre-populated via sessionStorage key 'leavewise.emailCompose.prefill':
 *   { recipients: [{email,firstName?,lastName?}], subject, body, source? }
 * — used by StalledSignups.js "Email them" buttons.
 */
const Compose = ({ onSent }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(null); // { count, sample }
  const [initialEmails, setInitialEmails] = useState([]);
  const [recipients, setRecipients] = useState({ extraEmails: [], listIds: [], segments: [] });

  // Pick up any prefill handed off by e.g. StalledSignups
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('leavewise.emailCompose.prefill');
      if (raw) {
        const p = JSON.parse(raw);
        if (p.subject) setSubject(p.subject);
        if (p.body) setBody(p.body);
        if (Array.isArray(p.recipients) && p.recipients.length > 0) {
          setInitialEmails(p.recipients.map(r => r.email));
        }
        sessionStorage.removeItem('leavewise.emailCompose.prefill');
      }
    } catch (_) {}
  }, []);

  const askToSend = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim()) { toast.error('Body is required'); return; }

    // Calculate final recipient count via the same server endpoint the
    // picker uses for its preview, so the confirmation matches reality.
    try {
      let count = recipients.extraEmails.length;
      const sample = [...recipients.extraEmails.slice(0, 5)];

      for (const listId of recipients.listIds) {
        const r = await axios.get(`/api/admin/emails/lists/${listId}/members`);
        count += (r.data.contacts || []).filter(c => !(c.unsubscribedAt || c.unsubscribed_at)).length;
        (r.data.contacts || []).slice(0, 5 - sample.length).forEach(c => sample.push(c.email));
      }

      for (const s of recipients.segments) {
        const r = await axios.post('/api/admin/emails/segment', s);
        count += r.data.count;
        (r.data.recipients || []).slice(0, 5 - sample.length).forEach(rec => sample.push(rec.email));
      }

      // Dedup expectation is approximate here — the server will dedupe
      // properly — but this is good enough for a sanity prompt.
      if (count === 0) { toast.error('No recipients selected'); return; }
      setConfirm({ count, sample: sample.slice(0, 5) });
    } catch (error) {
      toast.error('Failed to calculate recipients');
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      // Convert plain-text paragraphs to HTML preserving line breaks, OR
      // pass as-is if the body already contains HTML tags.
      const bodyHtml = /<\/?[a-z][\s\S]*>/i.test(body)
        ? body
        : `<div style="font-family: -apple-system, Segoe UI, Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #1f2937;">${
            body.split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('')
          }</div>`;

      const r = await axios.post('/api/admin/emails/send', {
        subject,
        bodyHtml,
        listIds: recipients.listIds,
        segments: recipients.segments,
        extraEmails: recipients.extraEmails
      });
      toast.success(`Campaign sent to ${r.data.recipientCount} recipient${r.data.recipientCount === 1 ? '' : 's'}`);
      setConfirm(null);
      setSubject('');
      setBody('');
      setRecipients({ extraEmails: [], listIds: [], segments: [] });
      setInitialEmails([]);
      if (onSent) onSent();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-3">
      <RecipientPicker
        value={recipients}
        onChange={setRecipients}
        initialEmails={initialEmails}
      />

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. New features in LeaveWise"
            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Body</label>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              <FiEye className="w-3 h-3" />
              {showPreview ? 'Edit' : 'Preview'}
            </button>
          </div>

          {showPreview ? (
            <div className="w-full min-h-[260px] px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white">
              {/* A quick visual preview — the actual send wraps this in the email shell + unsubscribe footer. */}
              {body.split(/\n\n+/).map((para, i) => (
                <p key={i} className="mb-2">
                  {para.split('\n').map((line, j) => (
                    <React.Fragment key={j}>
                      {line}
                      {j < para.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
              ))}
              <hr className="my-4 border-gray-200 dark:border-gray-700" />
              <p className="text-[11px] text-gray-500 text-center">
                You received this email from LeaveWise. Unsubscribe link appears here.
              </p>
            </div>
          ) : (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Plain text works fine. HTML also supported if you want control over formatting."
              rows={14}
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white font-mono resize-y"
            />
          )}
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Plain paragraphs auto-convert to HTML. An unsubscribe footer is appended automatically.
          </p>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
            <FiAlertCircle className="w-3 h-3" />
            Max 500 recipients per campaign. Unsubscribed contacts are excluded automatically.
          </p>
          <button
            type="button"
            onClick={askToSend}
            disabled={sending || !subject.trim() || !body.trim()}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
          >
            <FiSend className="w-4 h-4" />
            Review & Send
          </button>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 w-full max-w-md p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Send this campaign?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              About to send to <span className="font-semibold text-gray-900 dark:text-white">{confirm.count}</span> recipient{confirm.count === 1 ? '' : 's'}.
            </p>
            {confirm.sample.length > 0 && (
              <div className="mb-4 p-2.5 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">First few recipients</p>
                <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
                  {confirm.sample.map(e => <li key={e}>{e}</li>)}
                  {confirm.count > confirm.sample.length && (
                    <li className="text-gray-500 dark:text-gray-400 italic">… and {confirm.count - confirm.sample.length} more</li>
                  )}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setConfirm(null)}
                disabled={sending}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={doSend}
                disabled={sending}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {sending ? 'Sending…' : (<><FiSend className="w-3.5 h-3.5" />Send now</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Compose;
